import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Checkbox, Space, Typography } from 'antd'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { CreateMetricModal } from './CreateMetricModal'
import { MetricsPanelData } from './MetricsPanel'
import { DrilldownMetricConfig } from './MetricsPanelConfig'
import { useAvailableMetricConfigs } from './useAvailableMetricConfigs'

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

export const MetricsPanelConfigView: FC = () => {
  const {
    panelData: { activeMetrics, customMetrics },
    setPanelData,
  } = useVersionAwareDrilldownPanelData<MetricsPanelData>()

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingMetric, setEditingMetric] =
    useState<DrilldownMetricConfig | null>(null)

  const handleCheck = useCallback(
    (metricType: string, checked: boolean) => {
      setPanelData((prev) => {
        const newActiveMetrics = checked
          ? uniq([...prev.activeMetrics, metricType])
          : prev.activeMetrics.filter((t) => metricType !== t)
        return {
          ...prev,
          activeMetrics: newActiveMetrics,
        }
      })
    },
    [setPanelData]
  )

  const handleAddMetric = useCallback(
    async (config: DrilldownMetricConfig) => {
      setPanelData((prev) => ({
        ...prev,
        customMetrics: [...prev.customMetrics, config],
        activeMetrics: uniq([...prev.activeMetrics, config.metricType]),
      }))
    },
    [setPanelData]
  )

  const handleEditMetric = useCallback(
    async (config: DrilldownMetricConfig) => {
      setPanelData((prev) => ({
        ...prev,
        customMetrics: prev.customMetrics.map((metric) =>
          metric.metricType === editingMetric?.metricType ? config : metric
        ),
      }))
      setEditingMetric(null)
    },
    [setPanelData, editingMetric]
  )

  const handleDeleteMetric = useCallback(
    (metricType: string) => {
      setPanelData((prev) => ({
        ...prev,
        customMetrics: prev.customMetrics.filter(
          (metric) => metric.metricType !== metricType
        ),
        activeMetrics: prev.activeMetrics.filter((type) => type !== metricType),
      }))
    },
    [setPanelData]
  )

  const allAvailableMetrics = useAvailableMetricConfigs()

  const optionsByRolodexType: Record<string, readonly DrilldownMetricConfig[]> =
    useMemo(() => {
      return groupBy(allAvailableMetrics, 'rolodexType')
    }, [allAvailableMetrics])

  return (
    <div style={{ width: '100%' }}>
      <HeaderRow>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Available Metrics
        </Typography.Title>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => setIsAddModalOpen(true)}>
          Add Metric
        </Button>
      </HeaderRow>

      <Space orientation='vertical' size='small'>
        {Object.entries(optionsByRolodexType).map(
          ([rolodexType, metricConfigs]) => (
            <Space orientation='vertical' size='small' key={rolodexType}>
              <Typography.Title level={5}>{rolodexType}</Typography.Title>
              {metricConfigs.map((metricConfig) => {
                const isCustom = customMetrics?.some(
                  (custom) => custom.metricType === metricConfig.metricType
                )
                return (
                  <MetricRow key={metricConfig.metricType}>
                    <Checkbox
                      checked={activeMetrics.includes(metricConfig.metricType)}
                      onChange={(e) =>
                        handleCheck(metricConfig.metricType, e.target.checked)
                      }>
                      {metricConfig.title}
                      {isCustom && (
                        <Typography.Text
                          type='secondary'
                          style={{ fontSize: '11px', marginLeft: '8px' }}>
                          (Custom)
                        </Typography.Text>
                      )}
                    </Checkbox>
                    {isCustom && (
                      <Space size='small'>
                        <Button
                          type='text'
                          size='small'
                          icon={<EditOutlined />}
                          onClick={() => setEditingMetric(metricConfig)}
                          title='Edit metric'
                        />
                        <Button
                          type='text'
                          size='small'
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            handleDeleteMetric(metricConfig.metricType)
                          }
                          title='Delete metric'
                          danger
                        />
                      </Space>
                    )}
                  </MetricRow>
                )
              })}
            </Space>
          )
        )}
      </Space>

      <CreateMetricModal
        open={isAddModalOpen || !!editingMetric}
        onClose={() => {
          setIsAddModalOpen(false)
          setEditingMetric(null)
        }}
        onSave={editingMetric ? handleEditMetric : handleAddMetric}
        existingConfig={editingMetric}
        title={editingMetric ? 'Edit Metric' : 'Add Metric'}
      />
    </div>
  )
}
